import React, { useEffect, useState } from "react";
import { Button, Dropdown, Field, MessageBar, MessageBarBody, Option, Radio, RadioGroup, Spinner, Switch, Textarea } from "@fluentui/react-components";
import { patchMeeting, patchSettings } from "../spurt/meetings";
import type { MeetingSettings, MeetingView } from "../spurt/meetings";
import { errText } from "./meetingErrors";
import css from "./Meetings.module.css";

type Scope = "single" | "series";

const SCREEN: Record<MeetingSettings["screenShare"], string> = { everyone: "Everyone", presenters: "Presenters only", none: "No one" };
const CHAT: Record<MeetingSettings["chat"], string> = { everyone: "Everyone", hosts: "Hosts only", off: "Off" };
const ACCESS: Record<MeetingView["access"], string> = { invited: "Only people invited", org: "People in my organisation", anyone: "Anyone with the link" };
const BYPASS: Record<MeetingView["lobbyBypass"], string> = { everyone: "Everyone skips the waiting room", org: "People in my organisation", invited: "People invited", organizers: "Only organisers" };

function pick<T extends string>(label: string, value: T, labels: Record<T, string>, onPick: (v: T) => void, disabled: boolean) {
  return (
    <Field label={label}>
      <Dropdown aria-label={label} disabled={disabled} value={labels[value]} selectedOptions={[value]} onOptionSelect={(_, d) => d.optionValue && onPick(d.optionValue as T)}>
        {(Object.keys(labels) as T[]).map((k) => (
          <Option key={k} value={k}>
            {labels[k]}
          </Option>
        ))}
      </Dropdown>
    </Field>
  );
}

export interface SettingsSectionProps {
  meeting: MeetingView;
  onUpdated: (m: MeetingView) => void;
}

/** Host only. Changes that apply live (screen share, chat, unmute) go through the settings call; the rest through the edit call. */
const SettingsSection: React.FC<SettingsSectionProps> = ({ meeting, onUpdated }) => {
  const s = meeting.settings;
  const [screenShare, setScreenShare] = useState(s.screenShare);
  const [chat, setChat] = useState(s.chat);
  const [allowUnmute, setAllowUnmute] = useState(s.allowUnmute);
  const [lobbyMessage, setLobbyMessage] = useState(s.lobbyMessage ?? "");
  const [access, setAccess] = useState(meeting.access);
  const [lobbyBypass, setLobbyBypass] = useState(meeting.lobbyBypass);
  const [scope, setScope] = useState<Scope>(meeting.recurring || meeting.seriesId ? "series" : "single");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const series = !!(meeting.recurring || meeting.seriesId);
  const over = meeting.status === "ended" || meeting.status === "canceled";

  useEffect(() => {
    setScreenShare(meeting.settings.screenShare);
    setChat(meeting.settings.chat);
    setAllowUnmute(meeting.settings.allowUnmute);
    setLobbyMessage(meeting.settings.lobbyMessage ?? "");
    setAccess(meeting.access);
    setLobbyBypass(meeting.lobbyBypass);
  }, [meeting.settings, meeting.access, meeting.lobbyBypass]);

  const dirty =
    screenShare !== s.screenShare || chat !== s.chat || allowUnmute !== s.allowUnmute || lobbyMessage.trim() !== (s.lobbyMessage ?? "") || access !== meeting.access || lobbyBypass !== meeting.lobbyBypass;

  const save = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const topChanged = access !== meeting.access || lobbyBypass !== meeting.lobbyBypass || lobbyMessage.trim() !== (s.lobbyMessage ?? "");
      let updated: MeetingView;
      if (topChanged || (series && scope === "series")) {
        const settings: MeetingSettings = { ...s, screenShare, chat, allowUnmute };
        const lm = lobbyMessage.trim();
        if (lm) settings.lobbyMessage = lm;
        else delete settings.lobbyMessage;
        const target = series && scope === "series" ? (meeting.seriesId ?? meeting.id) : meeting.id;
        updated = await patchMeeting(target, { access, lobbyBypass, settings }, series ? scope : "single");
      } else {
        updated = await patchSettings(meeting.id, { screenShare, chat, allowUnmute });
      }
      onUpdated(updated);
      setMsg({ ok: true, text: "Settings saved." });
    } catch (e) {
      setMsg({ ok: false, text: errText(e, "change these settings") });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className={css.section} aria-label="Settings">
      <h3 className={css.h3}>Settings</h3>
      <div className={css.form}>
        {pick("Who can share their screen", screenShare, SCREEN, setScreenShare, over || busy)}
        {pick("Who can use the chat", chat, CHAT, setChat, over || busy)}
        <Switch label="Let people unmute themselves" checked={allowUnmute} disabled={over || busy} onChange={(_, d) => setAllowUnmute(d.checked)} />
        {pick("Who can join", access, ACCESS, setAccess, over || busy)}
        {pick("Who skips the waiting room", lobbyBypass, BYPASS, setLobbyBypass, over || busy)}
        <Field label="Message in the waiting room" hint={`${lobbyMessage.length}/500`}>
          <Textarea value={lobbyMessage} maxLength={500} disabled={over || busy} onChange={(_, d) => setLobbyMessage(d.value)} />
        </Field>
        {series && (
          <Field label="Apply changes to">
            <RadioGroup value={scope} onChange={(_, d) => setScope(d.value as Scope)} layout="horizontal">
              <Radio value="series" label="The whole series" />
              <Radio value="single" label="Only this meeting" />
            </RadioGroup>
          </Field>
        )}
      </div>
      {msg && (
        <MessageBar intent={msg.ok ? "success" : "error"} role={msg.ok ? "status" : "alert"}>
          <MessageBarBody>{msg.text}</MessageBarBody>
        </MessageBar>
      )}
      <div className={css.row}>
        <Button appearance="primary" disabled={over || busy || !dirty} icon={busy ? <Spinner size="tiny" /> : undefined} onClick={() => void save()}>
          Save settings
        </Button>
      </div>
    </section>
  );
};

export default SettingsSection;
